import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from './axiosInstance';
import { useFitToViewport } from './ShortTermForecast';
import './styles/PSIForecast.css';

/**
 * PSIForecast — Digital-signage Purchase-Sales-Inventory view.
 * Scoped to signage panels only (server filters ESL/gateway SKUs in buildPSI).
 *
 * Lays forecasted DEMAND (summed across every customer, straight from the
 * forecast engine) next to the supply side — incoming PO ARRIVALS and opening
 * inventory — and rolls a projected end-on-hand (EOH) forward per column:
 *
 *     EOH = previous EOH + Arrival - Shipment      (Shipment defaults to Demand)
 *
 * Any week/month EOH goes negative is a stockout (rendered red). All-up by
 * model code — inventory & POs are SAI-total, not per customer.
 */
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The four PSI rows, in the same order as the source spreadsheet.
const ROWS = [
  { key: 'demand', label: 'Demand' },
  { key: 'shipment', label: 'Shipment' },
  { key: 'arrival', label: 'Arrival' },
  { key: 'eoh', label: 'EOH' },
];

// Normalize an arrival date to YYYY-MM-DD. Excel re-saves dates as US M/D/YYYY,
// which the server's week/month bucketing can't match (it would silently drop
// the arrival), so convert on import.
function normalizeArrivalDate(s) {
  if (!s) return s;
  s = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;                 // already ISO
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);  // M/D/YYYY (US)
  if (m) {
    let [, mo, d, y] = m;
    if (y.length === 2) y = '20' + y;
    return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  const dt = new Date(s);
  if (!isNaN(dt)) return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  return s;                                                     // unrecognized — leave as-is
}

// True only for a real YYYY-MM-DD calendar date (rejects 2026-13-45 etc.).
function isValidIsoDate(s) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

function PSIForecast({ onOpenSnapshot }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showSupply, setShowSupply] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [editShip, setEditShip] = useState(null);   // `${modelCode}::${periodKey}` being edited
  const [shipVal, setShipVal] = useState('');
  const [editArr, setEditArr] = useState(null);     // same, for the Arrival row
  const [arrVal, setArrVal] = useState('');
  const scrollRef = useRef(null);
  // Reserve the page's bottom padding (.srf-container pad ~24px + card border) so
  // the fitted table never pushes the body past the viewport — otherwise the
  // page grows a second vertical scrollbar on top of the table's own.
  useFitToViewport(scrollRef, 28);

  // `silent` refreshes data in place without flipping the `loading` gate. A
  // non-silent reload unmounts the whole table for a "Loading..." stub, which
  // collapses the page and resets the scroll position — jarring after an inline
  // edit. Edits (shipment/arrival/import/clear) refresh silently; only the first
  // load and the manual Refresh button show the spinner.
  const fetchPSI = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await axiosInstance.get('/forecast/psi');
      setData(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching PSI:', err);
      setError('Failed to load PSI');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPSI(); }, [fetchPSI]);

  // ── supply entry (paste / file) ────────────────────────────────────────────
  // Accepts comma- or tab-separated rows:  modelCode, openingEOH, arrivalDate?, arrivalQty?
  // Returns { rows, errors, warnings } — malformed rows are reported, never
  // silently dropped (that's how a bad arrival date used to vanish).
  const parseSupplyText = (text) => {
    const bySku = {};
    const errors = [];
    const warnings = [];
    const knownCodes = new Set((data?.models || []).map(m => (m.modelCode || '').toUpperCase()).filter(Boolean));
    text.split(/\r?\n/).forEach((line, idx) => {
      const t = line.trim();
      if (!t) return;
      const [code, opening, aDate, aQty, sDate, sQty] = t.split(/[,\t]/).map(s => (s || '').trim());
      if (!code || /^model/i.test(code)) return;              // skip blanks / header row
      const ln = idx + 1;
      if (!bySku[code]) bySku[code] = { modelCode: code, openingEOH: 0, arrivals: [], shipmentOverrides: [] };

      if (knownCodes.size && !knownCodes.has(code.toUpperCase())) {
        warnings.push(`Line ${ln}: "${code}" isn't a digital-signage model — it won't appear on DS PSI.`);
      }

      if (opening !== '' && opening !== undefined) {
        if (isNaN(Number(opening))) errors.push(`Line ${ln} (${code}): on-hand "${opening}" isn't a number.`);
        else bySku[code].openingEOH = Number(opening);
      }

      const hasDate = aDate !== '' && aDate !== undefined;
      const hasQty = aQty !== '' && aQty !== undefined;
      if (hasDate || hasQty) {
        if (!hasDate) errors.push(`Line ${ln} (${code}): arrival qty "${aQty}" has no date.`);
        else if (!hasQty) errors.push(`Line ${ln} (${code}): arrival date "${aDate}" has no quantity.`);
        else if (isNaN(Number(aQty))) errors.push(`Line ${ln} (${code}): arrival qty "${aQty}" isn't a number.`);
        else {
          const iso = normalizeArrivalDate(aDate);
          if (!isValidIsoDate(iso)) errors.push(`Line ${ln} (${code}): "${aDate}" isn't a valid date — use YYYY-MM-DD (e.g. 2026-11-16).`);
          else bySku[code].arrivals.push({ date: iso, qty: Number(aQty) });
        }
      }

      const hasSDate = sDate !== '' && sDate !== undefined;
      const hasSQty = sQty !== '' && sQty !== undefined;
      if (hasSDate || hasSQty) {
        if (!hasSDate) errors.push(`Line ${ln} (${code}): shipment qty "${sQty}" has no date.`);
        else if (!hasSQty) errors.push(`Line ${ln} (${code}): shipment date "${sDate}" has no quantity.`);
        else if (isNaN(Number(sQty))) errors.push(`Line ${ln} (${code}): shipment qty "${sQty}" isn't a number.`);
        else {
          const iso = normalizeArrivalDate(sDate);
          if (!isValidIsoDate(iso)) errors.push(`Line ${ln} (${code}): shipment date "${sDate}" isn't valid — use YYYY-MM-DD.`);
          else bySku[code].shipmentOverrides.push({ period: iso, qty: Number(sQty) });
        }
      }
    });
    return { rows: Object.values(bySku), errors, warnings };
  };

  const importRows = async ({ rows, errors = [], warnings = [] }) => {
    if (errors.length) {
      alert(`Import stopped — fix ${errors.length} problem${errors.length > 1 ? 's' : ''} and try again:\n\n${errors.join('\n')}`);
      return;
    }
    if (!rows.length) { alert('No valid rows found. Format: modelCode, openingEOH, [arrivalDate, arrivalQty]'); return; }
    setBusy(true);
    try {
      const res = await axiosInstance.post('/forecast/psi/import', { rows });
      await fetchPSI({ silent: true });
      setShowSupply(false);
      setPasteText('');
      const warn = warnings.length ? `\n\nNote:\n${warnings.join('\n')}` : '';
      alert(`Imported supply for ${res.data.imported} model(s).${warn}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  // Per-cell Shipment override. Empty value removes the override (cell reverts
  // to Demand); a number (incl. 0) sets it. Period is the column key.
  const saveShipment = async (modelCode, period, raw) => {
    setEditShip(null);
    const v = String(raw).trim();
    if (v !== '' && isNaN(Number(v))) { alert('Shipment must be a number.'); return; }
    try {
      await axiosInstance.put(`/forecast/psi/${encodeURIComponent(modelCode)}/shipment`, { period, qty: v === '' ? null : Number(v) });
      fetchPSI({ silent: true });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update shipment');
    }
  };

  // Per-cell incoming arrival (PO). Empty clears it; a number sets what lands
  // that week/month (replaces any arrival already in that column).
  const saveArrival = async (modelCode, period, raw) => {
    setEditArr(null);
    const v = String(raw).trim();
    if (v !== '' && isNaN(Number(v))) { alert('Arrival must be a number.'); return; }
    try {
      await axiosInstance.put(`/forecast/psi/${encodeURIComponent(modelCode)}/arrival`, { period, qty: v === '' ? null : Number(v) });
      fetchPSI({ silent: true });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update arrival');
    }
  };

  const handlePaste = () => importRows(parseSupplyText(pasteText));

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => importRows(parseSupplyText(String(reader.result || '')));
    reader.readAsText(file);
    e.target.value = '';
  };

  // Download a ready-to-edit CSV template of the current models + their on-hand,
  // with the optional arrival columns in the header. Fill it in and re-import.
  const downloadSampleCsv = () => {
    const header = 'modelCode,openingEOH,arrivalDate,arrivalQty,shipmentDate,shipmentQty';
    const rows = (data?.models || [])
      .filter(m => m.modelCode)
      .map(m => `${m.modelCode},${m.openingEOH || 0}`);
    if (!rows.length) rows.push('S32B,0', 'B29C,0');
    const csv = `${header}\n${rows.join('\n')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ds-psi-supply-sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearSupply = async () => {
    if (!data) return;
    const withSupply = data.models.filter(m => m.hasSupply);
    if (!withSupply.length) { alert('No supply data to clear.'); return; }
    if (!window.confirm(`Clear entered supply for ${withSupply.length} model(s)?`)) return;
    setBusy(true);
    try {
      await Promise.all(withSupply.map(m => axiosInstance.delete(`/forecast/psi/${encodeURIComponent(m.modelCode || m.key)}`)));
      await fetchPSI({ silent: true });
    } catch (err) {
      alert('Clear failed');
    } finally {
      setBusy(false);
    }
  };

  // ── rendering helpers ───────────────────────────────────────────────────────
  const colHeader = (c) => {
    if (c.type === 'week') {
      const d = new Date(c.key + 'T00:00:00');
      return { line1: c.label, line2: `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}` };
    }
    const [, mo] = c.key.split('-'); // month column
    return { line1: c.label.split(' ')[0], line2: c.label.split(' ')[1] || `#${mo}` };
  };
  const fmt = (n, isEoh) => (isEoh ? Number(n).toLocaleString() : (n ? Number(n).toLocaleString() : '-'));
  const rowLabel = (m) => `${m.name}${m.bezel ? ` (${m.bezel === 'white' ? 'White' : m.bezel === 'red' ? 'Red' : 'Black'})` : ''}`;

  if (error) return <div className="srf-error">{error}</div>;

  const nCols = data ? data.columns.length : 0;

  // Current calendar week's Monday (local) → used to highlight that column.
  const currentWeekKey = (() => {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const isCurrentCol = (c) => c.type === 'week' && c.key === currentWeekKey;

  return (
    <div className="srf-store-types-section">
      <div className="srf-section-header psi-section-header">
        <div className="srf-section-header-right psi-controls">
          <button className="srf-btn srf-btn-primary" disabled={busy} onClick={() => setShowSupply(v => !v)}>
            {showSupply ? 'Close supply entry' : 'Enter / paste supply'}
          </button>
          <button className="srf-btn srf-btn-secondary" onClick={downloadSampleCsv} title="Download a CSV of the current models + on-hand to edit and re-import">
            Download sample CSV
          </button>
          <button className="srf-btn srf-btn-secondary" disabled={busy} onClick={clearSupply}>Clear supply</button>
          <button className="srf-btn srf-btn-secondary" disabled={busy} onClick={fetchPSI}>Refresh</button>
        </div>
      </div>

      <p className="srf-covered-legend">
        Demand is read-only from the forecast tabs. Enter on-hand + incoming POs below; <strong>EOH</strong> rolls
        forward per column — <span className="psi-neg-swatch">red</span> = stockout.
      </p>

      {showSupply && (
        <div className="psi-supply-panel">
          <div className="psi-supply-grid">
            <div>
              <label className="psi-supply-label">Paste rows (from Excel) — <code>modelCode, openingEOH, arrivalDate, arrivalQty, shipmentDate, shipmentQty</code></label>
              <textarea
                className="psi-supply-textarea"
                rows={5}
                placeholder={'S50B, 0, 2026-11-16, 90\nS55B, 97\nS65B, 96, , , 2027-03-01, 40'}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
              />
              <div className="psi-supply-actions">
                <button className="srf-btn srf-btn-primary" disabled={busy} onClick={handlePaste}>Apply pasted supply</button>
                <label className="srf-btn srf-btn-secondary psi-file-btn">
                  Import .csv file
                  <input type="file" accept=".csv,.txt" onChange={handleFile} hidden />
                </label>
              </div>
            </div>
            <div className="psi-supply-help">
              <strong>How it works</strong>
              <ul>
                <li><b>openingEOH</b> = on-hand today for that model.</li>
                <li>Each <b>arrivalDate / arrivalQty</b> = an incoming PO landing (add more lines for more POs). Dates as <b>YYYY-MM-DD</b>. Or just <b>click an Arrival cell</b> in the grid to set it for that week.</li>
                <li><b>Shipment</b> defaults to Demand; set <b>shipmentDate / shipmentQty</b> (or click a Shipment cell) to ship a different amount. EOH = prev + Arrival − Shipment.</li>
                <li>No spreadsheet handy? Hit <b>Download sample CSV</b> for a ready-to-edit template of the current models.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Stockout banner — the money shot */}
      {data && data.stockouts.length > 0 && (
        <div className="psi-banner psi-banner-warn">
          ⚠ {data.stockouts.length} model{data.stockouts.length > 1 ? 's' : ''} stock out —
          earliest <strong>{rowLabel(data.stockouts[0])}</strong> in <strong>{data.stockouts[0].column.label}</strong>
          {' '}({data.stockouts[0].shortfall.toLocaleString()} units).
        </div>
      )}
      {data && data.stockouts.length === 0 && data.models.some(m => m.hasDemand || m.hasSupply) && (
        <div className="psi-banner psi-banner-ok">✓ No stockouts — incoming supply covers forecasted demand across the horizon.</div>
      )}

      {loading ? (
        <div className="srf-loading">Loading...</div>
      ) : !data || data.models.length === 0 ? (
        <div className="srf-empty">No forecast data yet. Add demand on the Short-Term or Long-Term tabs.</div>
      ) : (
        <div className="srf-store-types-list">
          <div className="srf-store-type-card srf-ov-card">
            <div className="srf-monthly-grid srf-ov-readonly">
              <div className="srf-st-scroll" ref={scrollRef}>
                <div className="srf-ov-table psi-table">

                  {/* Header */}
                  <div className="srf-st-row srf-st-header-row">
                    <div className="srf-st-product-cell srf-st-header-cell">Model / Row</div>
                    {data.columns.map((c, i) => {
                      const h = colHeader(c);
                      const weekTrigger = c.type === 'week' && !!onOpenSnapshot;
                      return (
                        <div
                          key={c.key}
                          className={`srf-st-week-cell srf-st-header-cell ${c.type === 'month' ? 'srf-ov-month-cell' : ''} ${isCurrentCol(c) ? 'srf-week-current' : ''} ${weekTrigger ? 'srf-week-trigger' : ''}`}
                          onClick={weekTrigger ? () => onOpenSnapshot(c.key) : undefined}
                          role={weekTrigger ? 'button' : undefined}
                          tabIndex={weekTrigger ? 0 : undefined}
                          title={weekTrigger ? `Open snapshot of ${h.line1}` : undefined}
                          onKeyDown={weekTrigger ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenSnapshot(c.key); } } : undefined}
                        >
                          <div className="srf-st-week-label-1">{h.line1}</div>
                          <div className="srf-st-week-label-2">{h.line2}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Totals block */}
                  <div className="psi-model-header psi-total-header">Total — all models</div>
                  {ROWS.map(row => (
                    <div key={`tot-${row.key}`} className={`srf-st-row psi-row psi-row-${row.key}`}>
                      <div className="srf-st-product-cell psi-rowlabel">{row.label}</div>
                      {data.totals[row.key].map((v, i) => {
                        const neg = row.key === 'eoh' && v < 0;
                        return (
                          <div key={data.columns[i].key} className={`srf-st-week-cell ${data.columns[i].type === 'month' ? 'srf-ov-month-cell' : ''} ${neg ? 'psi-eoh-neg' : ''} ${isCurrentCol(data.columns[i]) ? 'srf-week-current' : ''}`}>
                            <div className={`srf-month-value ${v ? 'has-value' : ''}`}>{fmt(v, row.key === 'eoh')}</div>
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  {/* One block per model */}
                  {data.models.map(m => (
                    <React.Fragment key={m.key}>
                      <div className="psi-model-header">
                        <span className="psi-model-name">{rowLabel(m)}</span>
                        {m.modelCode && <span className="psi-model-code">{m.modelCode}</span>}
                        {m.stockoutAt !== null && <span className="psi-model-flag">stocks out {data.columns[m.stockoutAt].label}</span>}
                      </div>
                      {ROWS.map(row => (
                        <div key={`${m.key}-${row.key}`} className={`srf-st-row psi-row psi-row-${row.key}`}>
                          <div className="srf-st-product-cell psi-rowlabel">{row.label}</div>
                          {m[row.key].map((v, i) => {
                            const neg = row.key === 'eoh' && v < 0;
                            const col = data.columns[i];
                            const isShip = row.key === 'shipment' && !!m.modelCode;
                            const isArr = row.key === 'arrival' && !!m.modelCode;
                            const cellId = `${m.modelCode}::${col.key}`;
                            const overridden = isShip && m.shipOverridden && m.shipOverridden[i];
                            const editingShip = isShip && editShip === cellId;
                            const editingArr = isArr && editArr === cellId;
                            return (
                              <div key={col.key} className={`srf-st-week-cell ${col.type === 'month' ? 'srf-ov-month-cell' : ''} ${neg ? 'psi-eoh-neg' : ''} ${isCurrentCol(col) ? 'srf-week-current' : ''}`}>
                                {editingShip || editingArr ? (
                                  <input
                                    type="number"
                                    className="srf-month-input"
                                    value={editingShip ? shipVal : arrVal}
                                    autoFocus
                                    onChange={(e) => (editingShip ? setShipVal(e.target.value) : setArrVal(e.target.value))}
                                    onBlur={() => (editingShip ? saveShipment(m.modelCode, col.key, shipVal) : saveArrival(m.modelCode, col.key, arrVal))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') editingShip ? saveShipment(m.modelCode, col.key, shipVal) : saveArrival(m.modelCode, col.key, arrVal);
                                      if (e.key === 'Escape') { setEditShip(null); setEditArr(null); }
                                    }}
                                  />
                                ) : (
                                  <div
                                    className={`srf-month-value ${v ? 'has-value' : ''} ${(isShip || isArr) ? 'psi-ship-cell' : ''} ${overridden ? 'psi-ship-override' : ''}`}
                                    onClick={isShip ? () => { setEditShip(cellId); setShipVal(overridden ? String(v) : ''); } : isArr ? () => { setEditArr(cellId); setArrVal(v ? String(v) : ''); } : undefined}
                                    title={isShip ? (overridden ? 'Shipment override — click to change (clear the box to revert to Demand)' : 'Click to set shipment (defaults to Demand)') : isArr ? 'Click to set an incoming arrival (PO) landing this week/month (clear to remove)' : undefined}
                                  >
                                    {fmt(v, row.key === 'eoh')}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}

                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PSIForecast;
